import {
    Heading, HStack, IconButton, Text, VStack,
} from '@chakra-ui/react';
import { FaIcon, ServiceIcon, SkeletonMotion } from '@acp/core-ui';
import { faLockKeyhole, faLockKeyholeOpen, faTimes } from '@fortawesome/pro-light-svg-icons';
import { PortfolioServiceTag, ServiceIdMapper } from '@acp/shared';
import { useMemo } from 'react';
import { useProperty } from '../../../../property';
import { AccessIssuesWizardMachineContext } from '../machine/context';
import { useNextProblemSet } from '../hooks';

function AccessIssuesWizardHeader({
    showLockIcon = false,
    showCloseIcon = true,
}: {
    showLockIcon?: boolean;
    showCloseIcon?: boolean;
}) {
    const machine = AccessIssuesWizardMachineContext.useActorRef();

    const propertyId = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.context.propertyId
    );

    const isResolutionCompleted = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.matches('resolveIssueCompleted')
    );

    const nextProblemSet = useNextProblemSet();
    const property = useProperty(propertyId);

    const servicesDue = useMemo(() => property?.data?.services.filter(
        (service) => service.tags.includes(PortfolioServiceTag.BookedIn)
            || service.tags.includes(PortfolioServiceTag.Overdue)
            || service.tags.includes(PortfolioServiceTag.RebookingInProgress)
            || service.tags.includes(PortfolioServiceTag.ServicePending)
    ) ?? [], [property.data]);

    return (
        <HStack gap={4} alignItems="center">
            {
                showLockIcon && (
                    <FaIcon
                        icon={
                            isResolutionCompleted && nextProblemSet === null
                                ? faLockKeyholeOpen
                                : faLockKeyhole
                        }
                        w={7}
                        h={7}
                        color={isResolutionCompleted && nextProblemSet === null ? 'success.500' : 'danger.500'}
                    />
                )
            }
            <VStack gap={0} alignItems="start" flexGrow={1}>
                {property.data !== undefined && (
                    <Heading size="md">
                        {property.data.address}
                    </Heading>
                )}
                {property.data === undefined && (
                    <SkeletonMotion width="50%">
                        <Heading size="md">
                            hello!
                        </Heading>
                    </SkeletonMotion>
                )}
                {
                    servicesDue.length > 0 && (
                        <HStack color="muted.500" pt={1}>
                            <Text fontSize="sm">
                                Services due:
                            </Text>
                            {
                                ServiceIdMapper.toReorderedServices(servicesDue).map(
                                    (service) => (
                                        <ServiceIcon
                                            key={service.serviceId}
                                            service={service.shortName}
                                            height="20"
                                        />
                                    )
                                )
                            }
                        </HStack>
                    )
                }
            </VStack>
            {
                showCloseIcon && (
                    <IconButton
                        variant="link"
                        icon={<FaIcon icon={faTimes} />}
                        aria-label="Close"
                        onClick={() => machine.send({ type: 'CLOSE_MODAL' })}
                        color="black"
                    />
                )
            }
        </HStack>
    );
}

export { AccessIssuesWizardHeader };
